import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Box, Stack, Typography } from '@mui/material';
import type { ArchiveFormProgressResult } from './archive-form.types';
import {
  ArchiveSectionAnchorNav,
  type ArchiveWorkbenchSection,
} from './archive-section-anchor-nav.component';
import { ArchiveSaveProgressBar } from './archive-save-progress-bar.component';
import './archive-form.scss';

/** 建档表单工作台属性。 */
export interface ArchiveFormWorkbenchProps {
  /** 按页面展示顺序排列的建档分区。 */
  sections: readonly ArchiveWorkbenchSection[];
  /** 整张建档表单的完成与阻断状态。 */
  progress: ArchiveFormProgressResult;
  /** 底部保存及其他关联操作按钮。 */
  actions: React.ReactNode;
  /** 是否禁用底部操作区。 */
  disabled?: boolean;
  /** 是否由工作台渲染保存进度栏；外层已有底栏时关闭。 */
  showSaveProgress?: boolean;
  /** 是否将保存进度栏固定并与内容页水平边界对齐。 */
  fixedSaveProgress?: boolean;
  /** 锚点上方全宽内容（如 OCR 已填充提示横幅）。 */
  topContent?: React.ReactNode;
  /** 与表单分区并排的左侧栏；有值时锚点仍占满整行。 */
  sidePanel?: React.ReactNode;
}

/** 根据分区标识生成稳定的页面锚点。 */
const getSectionElementId = (key: string) => `archive-form-section-${key}`;

/**
 * 组合分区锚点导航、表单内容和底部保存进度条。
 * @param props - 工作台分区、完成状态、操作按钮与禁用状态
 */
export function ArchiveFormWorkbench({
  sections,
  progress,
  actions,
  disabled = false,
  showSaveProgress = true,
  fixedSaveProgress = false,
  topContent,
  sidePanel,
}: ArchiveFormWorkbenchProps) {
  // 当前位于视口导航区域的分区标识
  const [activeKey, setActiveKey] = useState(sections[0]?.key ?? '');
  // 工作台根元素，用于定位最近的页面内容容器
  const workbenchRef = useRef<HTMLDivElement>(null);
  // 固定保存栏的水平边界，来源于页面内容容器
  const [fixedSaveProgressStyle, setFixedSaveProgressStyle] = useState<React.CSSProperties>();

  useEffect(() => {
    // 分区集合变化且当前分区已不存在时，回退到首个分区
    if (!sections.some((section) => section.key === activeKey)) {
      setActiveKey(sections[0]?.key ?? '');
    }
  }, [activeKey, sections]);

  useEffect(() => {
    // 旧浏览器没有 IntersectionObserver 时保留点击定位能力
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return undefined;
    }

    // 观察器根据进入导航下方区域的分区反向更新高亮项
    const observer = new IntersectionObserver(
      (entries) => {
        // 当前批次首个可见分区
        const visibleEntry = entries.find((entry) => entry.isIntersecting);
        if (visibleEntry) {
          setActiveKey(visibleEntry.target.id.replace('archive-form-section-', ''));
        }
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 },
    );

    // 当前页面内可被观察的分区元素
    const sectionElements = sections
      .map((section) => document.getElementById(getSectionElementId(section.key)))
      .filter((element): element is HTMLElement => element !== null);

    sectionElements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [sections]);

  useLayoutEffect(() => {
    // 未启用固定模式时不读取外层布局，避免影响新增与弹窗场景
    if (!fixedSaveProgress || typeof window === 'undefined') {
      return undefined;
    }

    // 最近的页面内容容器决定固定底栏的水平位置与宽度
    const contentPage = workbenchRef.current?.closest('.content-page');
    if (!contentPage) {
      return undefined;
    }

    /** 将页面内容容器的最新边界同步给固定保存栏。 */
    const syncFixedSaveProgressBoundary = () => {
      // 当前内容页布局边界
      const { left, width } = contentPage.getBoundingClientRect();
      setFixedSaveProgressStyle({ left, width });
    };

    syncFixedSaveProgressBoundary();

    // 容器尺寸观察器用于响应侧栏和内容区布局变化
    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(syncFixedSaveProgressBoundary);
    resizeObserver?.observe(contentPage);
    window.addEventListener('resize', syncFixedSaveProgressBoundary);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', syncFixedSaveProgressBoundary);
    };
  }, [fixedSaveProgress]);

  /**
   * 高亮并平滑滚动到用户选择的分区。
   * @param key - 目标分区标识
   */
  const handleSectionChange = (key: string) => {
    setActiveKey(key);
    document.getElementById(getSectionElementId(key))?.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    });
  };

  /** 分区内容列表，可单独全宽或与侧栏并排。 */
  const sectionList = (
    <Stack className="archive-form__sections" spacing={3}>
      {sections.map((section) => (
        <Box
          component="section"
          className="archive-form__section"
          id={getSectionElementId(section.key)}
          key={section.key}
        >
          {section.sourceLabel ? (
            <Stack
              className="archive-form__section-heading"
              alignItems="flex-start"
            >
              <Typography variant="body2" color="text.secondary">
                {section.sourceLabel}
              </Typography>
            </Stack>
          ) : null}
          {section.content}
        </Box>
      ))}
    </Stack>
  );

  return (
    <Stack ref={workbenchRef} className="archive-form__workbench">
      {topContent}
      {sections.length > 0 ? (
        <ArchiveSectionAnchorNav
          activeKey={activeKey}
          sections={sections}
          onSectionChange={handleSectionChange}
        />
      ) : null}
      {sidePanel ? (
        <Box className="archive-ocr-split">
          {sidePanel}
          {sectionList}
        </Box>
      ) : (
        sectionList
      )}
      {showSaveProgress ? (
        <ArchiveSaveProgressBar
          progress={progress}
          actions={actions}
          disabled={disabled}
          fixed={fixedSaveProgress}
          style={fixedSaveProgress ? fixedSaveProgressStyle : undefined}
        />
      ) : null}
    </Stack>
  );
}
